import { useState, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { createMaterialGroup } from '../../services/storage';
import { FolderPlus } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  onSuccess: () => void;
}

export function CreateGroupDialog({
  open,
  onOpenChange,
  accessToken,
  onSuccess,
}: CreateGroupDialogProps) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const toast = useToast();

  const resetForm = () => {
    setName('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);

    try {
      await createMaterialGroup(accessToken, name.trim());
      toast.success('Grupo criado com sucesso!');
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5" />
            Novo Grupo de Materiais
          </DialogTitle>
          <DialogDescription>
            Crie um grupo para organizar materiais semelhantes. Itens podem ser
            agrupados automaticamente por nome ou manualmente atribuidos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="groupName">Nome do Grupo *</Label>
            <Input
              id="groupName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Acrilico Cristal, Chapas MDF..."
              required
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Use nomes descritivos que ajudem a identificar o tipo de material.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Criando...' : 'Criar Grupo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
